import { APIJobModel } from "../Models/API/JobModel"
import { useAppState } from "./useReduxState"
import { FetchState, getValue, hasValue } from "../Models/Client/FetchState"
import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { getJob, getJobWithMessage } from "../Actions/API/Job/JobActions"
import { useDereference } from "./useDereference"
import { Result } from "../Models/Utility/Result"
import { DataAccess } from "../Models/Client/DataAccess"
import { useAppDetailsPaths } from "./useAppDetailsPaths"

export function useJob(
    appID: string | undefined,
    jobID: string | undefined
): FetchState<APIJobModel> {
    const dispatch = useDispatch()
    const [isMessagesPath] = useAppDetailsPaths()
    const jobFetch = useAppState(state => {
        if (jobID === undefined || appID === undefined) {
            return
        }
        const jobTable = isMessagesPath
            ? state.api.jobs.messageTable
            : state.api.jobs.table
        const entry = jobTable[appID]

        return entry === undefined ? undefined : entry.records.get(jobID)
    })
    const dataAccess = useMemo(
        () =>
            jobID === undefined ? DataAccess.owner : DataAccess.perJob(jobID),
        [jobID]
    )
    const inputFetch = useDereference(
        jobID === undefined ? undefined : getValue(jobFetch)?.input,
        dataAccess
    )

    useEffect(() => {
        if (
            appID === undefined ||
            jobID === undefined ||
            jobFetch !== undefined
        ) {
            return
        }
        if (isMessagesPath) {
            dispatch(getJobWithMessage(appID, jobID))
        } else {
            dispatch(getJob(appID, jobID))
        }
    }, [dispatch, jobFetch, appID, jobID, isMessagesPath])

    return useMemo(() => {
        if (!hasValue(jobFetch)) {
            return jobFetch
        }

        if (!hasValue(inputFetch)) {
            return jobFetch
        }

        return Result.ok({
            ...jobFetch.value,
            input: inputFetch.value
        })
    }, [inputFetch, jobFetch])
}
